import { useEffect, useState, useCallback } from "react";
import { api } from "../api";
import {
  KIND_TO_DEFAULT,
  SEED_ONLY_KINDS,
  toDisplayList,
  activityCodeMeta,
  eventTypeMeta,
  taskStatusMeta,
  eventStatusMeta, // 136 §3
} from "../utils/taxonomyMap";
import { useTranslation } from "../i18n";
import { publishTaxonomy } from "../lib/taxonomyRegistry";
import type { TaxonomyEntry } from "@shared/entities";

/* 181 B.2a [RULED 2026-09-11, Laurent, ruling 7] — WHAT A SERVED ROW CARRIES
   BEYOND THE COLUMNS. `GET /taxonomies?kind=` adds `refusal`: the answer of
   `deleteRefusal(kind, code)` on the server — the same function the
   reference-count route and the DELETE handler call — in its exact shape, or
   null for a deletable row and for a divider. COMPOSED here rather than added
   to `shared/entities.ts`: that file is GENERATED from schema.prisma and held
   byte-fresh by entities-drift (its header says "compose when a relation is
   loaded"), and a wire-only field is not a column. The bundled fallback rows
   (`toEntriesFromDefault`) carry no `refusal`, which reads as "not refused"
   while the server still refuses — the enforcing end is the DELETE handler. */
export interface TaxonomyDeleteRefusal {
  code: "taxonomy.delete_refused_fixed" | "taxonomy.delete_refused_protected";
  kind: string;
  entryCode: string;
}
export type ServedTaxonomyEntry = TaxonomyEntry & { refusal?: TaxonomyDeleteRefusal | null };

// Module-level cache: one record per taxonomy kind.
// Shape: { data: TaxonomyEntry[] | null, loading, error, lastFetch,
//          lastAttempt, inflight, fellBack }
const cache = new Map();
// 110 §2 — how long a FAILED fetch stands before a subscriber's render may
// try again. Small enough that the outage window heals at the next glance,
// large enough that a down API is not hammered on every keystroke.
const FELLBACK_RETRY_MS = 10_000;
// Subscribers: kind → Set<forceRender>. Used by invalidate() + every useTaxonomy
// mount. A single cache update broadcasts to every component that reads the
// kind, so Settings edits show up in the Case modals without a page reload.
const subscribers = new Map();
// Whether we've already console.warn'd about a fallback for this kind, so the
// log stays readable during long-running outages.
const warned = new Set();

function record(kind: any) {
  let rec = cache.get(kind);
  if (!rec) {
    rec = {
      data: null,
      loading: false,
      error: null,
      lastFetch: 0,
      lastAttempt: 0,
      inflight: null,
      fellBack: false,
    };
    cache.set(kind, rec);
  }
  return rec;
}

function notify(kind: any) {
  const set = subscribers.get(kind);
  if (!set) return;
  for (const cb of set) cb();
}

async function fetchKind(kind: any) {
  const rec = record(kind);
  if (rec.inflight) return rec.inflight;
  rec.loading = true;
  rec.error = null;
  rec.lastAttempt = Date.now(); // 110 §2 — the retry cooldown's anchor
  rec.inflight = (async () => {
    try {
      const rows = await api.get(
        `/taxonomies?kind=${encodeURIComponent(kind)}`,
      );
      rec.data = Array.isArray(rows) ? rows : [];
      publishTaxonomy(kind, rec.data);
      rec.fellBack = false;
      rec.lastFetch = Date.now();
    } catch (e) {
      rec.error = e;
      // Fall back to the bundled DEFAULT_* for this kind so the app remains
      // functional through a backend outage or a stale-JWT 401 window — except
      // for a SEED_ONLY kind, where the bundled list is provisioning canon and
      // substituting it would render a ladder the firm never defined. Those
      // fall back to nothing, and the badges say so.
      const dflt = SEED_ONLY_KINDS.has(kind) ? [] : KIND_TO_DEFAULT[kind] || [];
      rec.data = toEntriesFromDefault(kind, dflt);
      publishTaxonomy(kind, rec.data);
      rec.fellBack = true;
      if (!warned.has(kind)) {
        warned.add(kind);
        // eslint-disable-next-line no-console
        console.warn(
          `[useTaxonomy] ${kind} fetch failed; using bundled DEFAULT_* fallback.`,
          e && (e as any).message,
        );
      }
    } finally {
      rec.loading = false;
      rec.inflight = null;
      notify(kind);
    }
  })();
  return rec.inflight;
}

// When we fall back to the DEFAULT_* constant, wrap it in the TaxonomyEntry
// shape so downstream consumers receive a consistent object shape. ids are
// synthetic negatives so they never collide with server rows.
function toEntriesFromDefault(kind: any, dflt: any) {
  /* 110 §2 — a default that already carries ENTRY-shaped rows (code +
     multilingual labels) passes through with synthetic ids. party_role is
     the first such kind: it deliberately has no display-list shape (its
     consumers read ENTRIES), so the shape switch below cannot wrap it — and
     before this branch its outage fallback was the empty list, which is what
     put "No match" in every role picker for a whole session. */
  if (
    Array.isArray(dflt) &&
    dflt.length &&
    typeof dflt[0] === "object" &&
    dflt[0] &&
    "labelEn" in dflt[0]
  )
    return dflt.map((x: Record<string, unknown>, i: number) => ({
      id: -(i + 1),
      kind,
      parentId: null,
      order: i,
      archived: false,
      system: true,
      meta: null,
      ...x,
    }));
  const shape = (toDisplayList as any).shapeOf(kind);
  // 05n — the `string` branch is gone with the shape family it served. It
  // re-derived a code from a label with a slug() this file kept its own copy of
  // — a third slugifier beside the seed's and the route's, and the one that
  // could silently disagree with the row it was standing in for. The six kinds
  // it covered now carry their code in the canon and take the `tuple` branch.
  // 60 §4c — the tuple branch serves thirteen kinds and only `task_status`
  // declares meta, so the derivation is asked rather than the kind hardcoded:
  // `taskStatusMeta` answers null for every other tuple kind and for a firm's
  // own seventh status. This is the third consumer of one derivation, the same
  // contract activityCodeMeta and eventTypeMeta already hold — the answer is
  // unchanged either way (a row declaring nothing falls back to
  // BUILTIN_TERMINAL_STATUSES, which is derived from the same map), and the
  // point is that the outage path emits the rows the server would.
  if (shape === "tuple")
    return dflt.map((t: any, i: any) => ({
      id: -(i + 1),
      kind,
      code: t[0],
      label: t[1],
      parentId: null,
      order: i,
      archived: false,
      system: true,
      /* 136 §3 — `event_status` is the second tuple kind to declare meta, on
         the same contract: gated on the KIND because `cancelled` and
         `archived` are codes of `task_status` too, and a by-code answer
         cannot tell the two planes apart. */
      meta:
        kind === "task_status"
          ? taskStatusMeta(t)
          : kind === "event_status"
            ? eventStatusMeta(t)
            : null,
    }));
  if (shape === "activity")
    return dflt.map((x: any, i: any) => ({
      id: -(i + 1),
      kind,
      code: x.code,
      label: x.desc,
      parentId: null,
      order: i,
      archived: false,
      system: true,
      // Same meta the seed writes (default Eisenhower quadrant), so a
      // taxonomy outage keeps eisenAuto's answers identical to a live fetch.
      meta: activityCodeMeta(x),
    }));
  if (shape === "event")
    return dflt.map((x: any, i: any) => ({
      id: -(i + 1),
      kind,
      code: x.code,
      label: x.desc,
      parentId: null,
      order: i,
      archived: false,
      system: true,
      // Same meta the seed writes (colour + the autofilled-location spec), so
      // a taxonomy outage keeps the modal's autofill identical to a live fetch.
      meta: eventTypeMeta(x),
    }));
  if (shape === "vlc")
    return dflt.map((x: any, i: any) => ({
      id: -(i + 1),
      kind,
      code: x.v,
      label: x.l,
      parentId: null,
      order: i,
      archived: false,
      system: true,
      meta: { color: x.c },
    }));
  if (shape === "link")
    return dflt.map((x: any, i: any) => ({
      id: -(i + 1),
      kind,
      code: x.id,
      label: x.name,
      parentId: null,
      order: i,
      archived: false,
      system: true,
      meta: { icon: x.icon, iconColor: x.iconColor, nameColor: x.nameColor },
    }));
  if (shape === "external")
    return dflt.map((x: any, i: any) => ({
      id: -(i + 1),
      kind,
      code: x.id,
      label: x.name,
      parentId: null,
      order: i,
      archived: false,
      system: true,
      meta: { icon: x.icon, url: x.url },
    }));
  return [];
}

export function useTaxonomy(kind: any, opts: any = {}) {
  const { includeArchived = false } = opts;
  const [, setTick] = useState(0);
  // Phase M+3.7 — subscribe to language changes so consumers re-render
  // when the user switches lang. The pickEntryLabel helper reads
  // window.__lxLang lazily, so we just need a render trigger.
  const { lang } = useTranslation();

  useEffect(() => {
    if (!kind) return undefined;
    if (!subscribers.has(kind)) subscribers.set(kind, new Set());
    const set = subscribers.get(kind);
    const rerender = () => setTick((t) => t + 1);
    set.add(rerender);
    const rec = record(kind);
    /* 110 §2 — A RECORD THAT FELL BACK RETRIES. `rec.data === null` alone
       meant one failed fetch cached the bundled fallback for the whole
       browser session: the 502 window of a pm2 restart left every picker of
       an affected kind on its fallback — and a kind with NO bundled default
       on "No match" — until a full reload (the B(i) ghost, measured in §0).
       Now the next MOUNT after an outage refetches (inflight-guarded, as
       before), so the kind heals itself the moment the API is back. */
    if ((rec.data === null || rec.fellBack) && !rec.inflight) fetchKind(kind);
    return () => {
      set.delete(rerender);
    };
  }, [kind]);

  /* 110 §2, SECOND HALF — MEASURED: the mount-retry above cannot heal
     party_role, the ghost's own kind, because its ONLY live subscription is
     App-level (App.tsx `useTaxonomy("party_role")`, threaded down since 109
     §7a) and App never re-mounts. So a fallen-back kind also retries from a
     subscriber's ordinary re-render — any navigation or state change — behind
     a cooldown, so a dead API is asked at most once per FELLBACK_RETRY_MS per
     kind. Dep-less on purpose: the guard is the condition, not the deps. */
  useEffect(() => {
    if (!kind) return;
    const rec2 = record(kind);
    if (
      rec2.fellBack &&
      !rec2.inflight &&
      Date.now() - (rec2.lastAttempt || 0) > FELLBACK_RETRY_MS
    )
      fetchKind(kind);
  });

  const rec = record(kind);
  const raw = rec.data || [];
  const entries = (includeArchived ? raw : raw.filter((e: any) => !e.archived))
    .slice()
    .sort((a: any, b: any) => a.order - b.order || a.id - b.id);

  const refresh = useCallback(() => {
    invalidate(kind);
  }, [kind]);

  return {
    entries,
    loading: rec.loading,
    error: rec.error,
    fellBack: rec.fellBack,
    refresh,
    lang,
  };
}

/** Convenience: returns the compat-shaped display list (legacy DEFAULT_* shape) for a kind. */
export function useTaxonomyList(kind: any) {
  // Pull lang from useTaxonomy so React knows to re-derive when it flips.
  const { entries } = useTaxonomy(kind);
  return toDisplayList(entries, kind);
}

export function invalidate(kind: any) {
  const rec = record(kind);
  rec.data = null;
  publishTaxonomy(kind, null);
  rec.lastFetch = 0;
  warned.delete(kind);
  fetchKind(kind);
}

export function invalidateAll() {
  for (const kind of cache.keys()) invalidate(kind);
}

// Phase M+7 — soft refetch that keeps existing data visible while the
// background fetch is in flight. Use this for in-place row edits (label,
// meta) so subscriber components keyed by row id don't unmount/remount on
// every patch — that churn was closing the SubtaskTemplateEditor mid-add.
export function reload(kind: any) {
  const rec = record(kind);
  rec.lastFetch = 0;
  warned.delete(kind);
  fetchKind(kind);
}

export function reloadAll() {
  for (const kind of cache.keys()) reload(kind);
}

/* 207C §5 — A RELOAD THAT STARTS AFTER THE CALL. `fetchKind` hands back a fetch
   already in flight (`rec.inflight`), and that fetch may have left BEFORE the
   write the caller just had answered — so `reload()` right after a save can
   land the list as it was before it. This waits for such a fetch, then fetches
   again, and settles when the kind's rows are the ones the server holds now
   (src/lib/taxonomyMetaSaves.ts waits on it before it lets typed text go). */
export async function reloadFresh(kind: string): Promise<void> {
  const rec = record(kind) as { inflight: Promise<unknown> | null; lastFetch: number };
  if (rec.inflight) await rec.inflight;
  rec.lastFetch = 0;
  warned.delete(kind);
  await fetchKind(kind);
}

/** Test-only: reset module state between tests. */
export function __resetTaxonomyCache() {
  cache.clear();
  subscribers.clear();
  warned.clear();
}
